import { ValuationMetrics } from '@/types/valuation';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  TrendingUpIcon, 
  TrendingDownIcon, 
  DollarSignIcon,
  BarChart3Icon,
  PieChartIcon,
  InfoIcon,
  LineChartIcon,
  Coins
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUSD, formatRatio, formatPercent, formatPercentSimple } from '@/lib/format';
import { TvlHistoryChart } from './TvlHistoryChart';
import { CategoryBadge } from './CategoryBadge';

interface ValuationDrawerProps {
  asset: ValuationMetrics | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ValuationDrawer({ asset, isOpen, onClose }: ValuationDrawerProps) {
  if (!asset) return null;

  return (
    <Sheet open={isOpen} onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="space-y-4">
          <div className="flex items-center gap-3">
            {asset.image && (
              <img src={asset.image} alt={asset.name} className="h-12 w-12 rounded-full" />
            )}
            <div>
              <SheetTitle className="text-xl">{asset.name}</SheetTitle>
              <SheetDescription className="text-base">
                {asset.symbol} • Rank #{asset.rank}
              </SheetDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryBadge category={asset.defi_category || asset.category} size="md" />
            {asset.protocol_name !== asset.name && (
              <Badge variant="secondary">{asset.protocol_name}</Badge>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Price & Market Data */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSignIcon className="h-5 w-5" />
                Market Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Price</p>
                  <p className="text-xl font-bold">{formatUSD(asset.price, 4)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Market Cap</p>
                  <p className="text-lg font-semibold">{formatUSD(asset.market_cap || asset.mcap_usd)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">24h Change</span>
                    {asset.change_24h >= 0 ? <TrendingUpIcon className="h-3 w-3 text-success" /> : <TrendingDownIcon className="h-3 w-3 text-destructive" />}
                  </div>
                  <p className={cn(
                    "text-lg font-semibold",
                    asset.change_24h >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {formatPercent(asset.change_24h)}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">7d Change</span>
                    {(asset.change_7d || 0) >= 0 ? <TrendingUpIcon className="h-3 w-3 text-success" /> : <TrendingDownIcon className="h-3 w-3 text-destructive" />}
                  </div>
                  <p className={cn(
                    "text-lg font-semibold",
                    (asset.change_7d || 0) >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {formatPercent(asset.change_7d)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Fully Diluted Valuation</p>
                <p className="text-lg font-semibold">{formatUSD(asset.fdv)}</p>
              </div>
            </CardContent>
          </Card>

          {/* DeFi Metrics */}
          {(asset.data_flags.has_tvl || asset.data_flags.has_fees || asset.data_flags.has_revenue) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3Icon className="h-5 w-5" />
                  DeFi Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {asset.data_flags.has_tvl && (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Total Value Locked (TVL)</p>
                      <span className={cn(
                        "text-sm",
                        asset.deltas.tvl_7d >= 0 ? "text-success" : "text-destructive"
                      )}>
                        7d: {formatPercent(asset.deltas.tvl_7d)}
                      </span>
                    </div>
                    <p className="text-xl font-bold">{formatUSD(asset.tvl)}</p>
                  </div>
                )}

                {asset.data_flags.has_fees && (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Fees (7d)</p>
                      <span className={cn(
                        "text-sm",
                        asset.deltas.fees_7d >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {formatPercent(asset.deltas.fees_7d)}
                      </span>
                    </div>
                    <p className="text-lg font-semibold">{formatUSD(asset.fees_7d)}</p>
                    <p className="text-xs text-muted-foreground">
                      Annualized: {formatUSD(asset.fees_annualized)}
                    </p>
                  </div>
                )}

                {asset.data_flags.has_revenue && (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Revenue (7d)</p>
                      <span className={cn(
                        "text-sm",
                        asset.deltas.revenue_7d >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {formatPercent(asset.deltas.revenue_7d)}
                      </span>
                    </div>
                    <p className="text-lg font-semibold">{formatUSD(asset.revenue_7d)}</p>
                    <p className="text-xs text-muted-foreground">
                      Annualized: {formatUSD(asset.revenue_annualized)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fees Extended */}
          {(asset.fees_30d || asset.fees_all_time || asset.fees_change_1d != null) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Coins className="h-5 w-5" />
                  Fees Extended
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  {asset.fees_7d && (
                    <div>
                      <p className="text-sm text-muted-foreground">Fees 7d</p>
                      <p className="text-lg font-semibold">{formatUSD(asset.fees_7d)}</p>
                    </div>
                  )}
                  {asset.fees_30d && (
                    <div>
                      <p className="text-sm text-muted-foreground">Fees 30d</p>
                      <p className="text-lg font-semibold">{formatUSD(asset.fees_30d)}</p>
                    </div>
                  )}
                </div>
                
                {asset.fees_all_time && (
                  <div>
                    <p className="text-sm text-muted-foreground">Fees All Time</p>
                    <p className="text-xl font-bold text-primary">{formatUSD(asset.fees_all_time)}</p>
                  </div>
                )}

                {asset.fees_change_1d != null && (
                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <p className="text-sm text-muted-foreground">Fees Δ 24h</p>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "font-medium",
                        asset.fees_change_1d >= 0 
                          ? "bg-profit/10 text-profit border-profit/30" 
                          : "bg-loss/10 text-loss border-loss/30"
                      )}
                    >
                      {asset.fees_change_1d >= 0 ? <TrendingUpIcon className="h-3 w-3 mr-1" /> : <TrendingDownIcon className="h-3 w-3 mr-1" />}
                      {formatPercent(Math.abs(asset.fees_change_1d))}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* TVL History Chart */}
          {asset.data_flags.has_tvl && asset.data_flags.tvl_source_label && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <LineChartIcon className="h-5 w-5" />
                  TVL History (90d)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TvlHistoryChart 
                  slug={asset.data_flags.tvl_source_label} 
                  days={90} 
                  height={180}
                />
              </CardContent>
            </Card>
          )}

          {/* Valuation Ratios */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <PieChartIcon className="h-5 w-5" />
                Valuation Ratios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">P/Fees Ratio</p>
                  <p className="text-lg font-semibold">{formatRatio(asset.p_fees)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">P/Revenue Ratio</p>
                  <p className="text-lg font-semibold">{formatRatio(asset.p_revenue)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">FDV/Fees Ratio</p>
                  <p className="text-lg font-semibold">{formatRatio(asset.fdv_fees)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">FDV/Revenue Ratio</p>
                  <p className="text-lg font-semibold">{formatRatio(asset.fdv_revenue)}</p>
                </div>
              </div>

              {(asset.fees_tvl || asset.revenue_tvl) && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    {asset.fees_tvl && (
                      <div>
                        <p className="text-sm text-muted-foreground">Fees/TVL Yield</p>
                        <p className="text-lg font-semibold text-success">
                          {formatPercentSimple(asset.fees_tvl * 100, 2)}
                        </p>
                      </div>
                    )}
                    {asset.revenue_tvl && (
                      <div>
                        <p className="text-sm text-muted-foreground">Revenue/TVL Yield</p>
                        <p className="text-lg font-semibold text-success">
                          {formatPercentSimple(asset.revenue_tvl * 100, 2)}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Explanation */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <InfoIcon className="h-5 w-5" />
                Metric Explanations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="font-medium">P/Fees Ratio</p>
                <p className="text-muted-foreground">Market Cap divided by annualized fees. Lower values may indicate better value.</p>
              </div>
              <div>
                <p className="font-medium">P/Revenue Ratio</p>
                <p className="text-muted-foreground">Market Cap divided by annualized revenue. Similar to P/E ratio in traditional markets.</p>
              </div>
              <div>
                <p className="font-medium">Fees/TVL & Revenue/TVL</p>
                <p className="text-muted-foreground">Capital efficiency metrics showing protocol's ability to generate income from locked capital.</p>
              </div>
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> These are approximated metrics. Not all tokens directly capture fees/revenue from their protocols. 
                  Data availability varies by protocol and should be considered when making investment decisions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
